import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import {
  dryRunImportOrgFromEdlinkDistrictEntry,
  getOrgs,
  getUnimportedEdlinkOrgEntries,
  importOrgFromEdlinkDistrictEntry,
} from '@/lib/api-calls';
import { edlinkIdSchema, EdlinkOrgChartDryRunResult, EdlinkOrgChartImportSummary } from '@airreading/common/data';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { Button } from '@/components/ui/button';
import { ImportSummary } from './import-summary';

const formSchema = z.object({
  districtId: edlinkIdSchema,
});

type ImportFromEdlinkProps = {
  trigger: React.ReactNode;
};

type EdlinkImportResult =
  | { type: 'success'; summary: EdlinkOrgChartImportSummary | EdlinkOrgChartDryRunResult }
  | { type: 'error'; message: string };

function EdlinkImportResultView({ result }: { result: EdlinkImportResult | null }) {
  if (!result) {
    return null;
  }

  if (result.type === 'error') {
    return (
      <Alert variant="destructive">
        <AlertDescription>{result.message}</AlertDescription>
      </Alert>
    );
  }

  return <ImportSummary summary={result.summary} />;
}

export const ImportFromEdlink = ({ trigger }: ImportFromEdlinkProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: importOrgFromEdlinkDistrictEntry,
  });

  const { mutateAsync: dryRunMutate, isPending: isDryRunPending } = useMutation({
    mutationFn: dryRunImportOrgFromEdlinkDistrictEntry,
  });

  const { data: viableEdlinkDistrictEntries } = useQuery(getUnimportedEdlinkOrgEntries());
  const [edlinkImport, setEdlinkImport] = useState<EdlinkImportResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<EdlinkImportResult | null>(null);
  const queryClient = useQueryClient();

  const hasSuccess = edlinkImport?.type === 'success' || dryRunResult?.type === 'success';

  const getEntry = (districtId: string) =>
    viableEdlinkDistrictEntries?.find((entry) => entry.districtId === districtId);

  const onSubmit = async ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    const entry = getEntry(formData.districtId);
    if (!entry) {
      setEdlinkImport({ type: 'error', message: `Selected unknown Edlink district ID: ${formData.districtId}` });
      return;
    }

    try {
      const summary = await mutateAsync(entry);
      setEdlinkImport({ type: 'success', summary });
      queryClient.invalidateQueries(getOrgs());
      queryClient.invalidateQueries(getUnimportedEdlinkOrgEntries());
    } catch {
      setEdlinkImport({
        type: 'error',
        message: 'Edlink district import failed. Please try again or contact support.',
      });
    }
  };

  const onClose = () => {
    setEdlinkImport(null);
    setDryRunResult(null);
  };

  const isActionDisabled = (districtId: string, isValid: boolean, isSubmitting: boolean) =>
    !isValid ||
    !districtId ||
    !viableEdlinkDistrictEntries?.some((e) => e.districtId === districtId) ||
    isDryRunPending ||
    isSubmitting;

  const onDownloadSummary = () => console.log('Download summary - not yet implemented');

  const onDryRun = (districtId: string) => {
    const entry = getEntry(districtId);

    if (!entry) {
      return;
    }

    setDryRunResult(null);
    dryRunMutate(entry)
      .then((summary) => setDryRunResult({ type: 'success', summary }))
      .catch(() => setDryRunResult({ type: 'error', message: 'Dry run failed. Please try again.' }));
  };

  return (
    <FormModal
      modalTitle="Sync Edlink district"
      onSubmit={onSubmit}
      onClose={onClose}
      confirmBtnLabel="Sync"
      submitButtonProps={(form) => {
        const districtId = form.watch('districtId');
        const { isValid, isSubmitting } = form.formState;
        return {
          loading: isSubmitting,
          disabled: isActionDisabled(districtId, isValid, isSubmitting),
        };
      }}
      formSchema={formSchema}
      trigger={trigger}
      isFullScreen={hasSuccess}
      preventExternalClose
      footerContent={
        viableEdlinkDistrictEntries?.length
          ? ({ form }) => {
              const districtId = form.watch('districtId');
              const { isValid, isSubmitting } = form.formState;
              return (
                <>
                  {hasSuccess && (
                    <Button type="button" variant="secondary" onClick={onDownloadSummary}>
                      {edlinkImport?.type === 'success' ? 'Download Summary' : 'Download Dry Run Summary'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    loading={isDryRunPending}
                    disabled={isActionDisabled(districtId, isValid, isSubmitting)}
                    onClick={() => onDryRun(form.getValues('districtId'))}
                  >
                    Dry Run
                  </Button>
                </>
              );
            }
          : undefined
      }
    >
      {({ form }) => {
        const { isSubmitting } = form.formState;
        return (
          <>
            <div className="grid gap-4 py-4">
              {(() => {
                if (viableEdlinkDistrictEntries === null) {
                  return (
                    <Alert variant="destructive">
                      <AlertDescription>Could not connect to Edlink API. Please contact engineering.</AlertDescription>
                    </Alert>
                  );
                }
                return null;
              })()}
              {viableEdlinkDistrictEntries ? (
                <FormSingleSelect
                  label="Edlink ID"
                  options={viableEdlinkDistrictEntries.map((entry) => entry.districtId)}
                  renderOption={(districtId) => {
                    const entry = viableEdlinkDistrictEntries.find((entry) => entry.districtId === districtId);
                    return entry ? (
                      <>
                        {entry.districtName} -{' '}
                        <EdlinkIdCode code={districtId} showCopyButton={false} showColor={false} />
                      </>
                    ) : (
                      districtId
                    );
                  }}
                  form={form}
                  name="districtId"
                  selectProps={{ disabled: isSubmitting || isDryRunPending }}
                />
              ) : null}
              <EdlinkImportResultView result={dryRunResult} />
              <EdlinkImportResultView result={edlinkImport} />
            </div>
          </>
        );
      }}
    </FormModal>
  );
};
